import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Grid,
  TextField,
  Button,
  Box,
  Paper,
  Divider,
  Chip,
  CircularProgress,
  Alert,
  FormControlLabel,
  Switch,
  Tabs,
  Tab,
  IconButton,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Save as SaveIcon,
  Message as MessageIcon,
  Info as InfoIcon,
  Code,
  Visibility,
  Close,
} from '@mui/icons-material';
import { THEME_COLORS, OPERATION_STATUS, UX_TIMING } from '../../constants';
import { useSubscriptionTemplates } from '../../hooks/useSubscriptionTemplates';
import ConfirmationModal from '../forms/ConfirmationModal';
import SubscriptionTemplateVariablesPicker from '../forms/SubscriptionTemplateVariablesPicker';
import subscriptionTemplateVariableService from '../../services/subscriptionTemplateVariableService';

const templateMetadata = {
  subscription_activated: {
    label: 'Suscripción Activada',
    helperText: 'Se envía cuando una nueva suscripción se marca como activa.',
  },
  subscription_lapsing_7_days: {
    label: 'Aviso de Vencimiento (7 Días)',
    helperText: 'Primer aviso, se envía 7 días antes de que la suscripción expire.',
  },
  subscription_lapsing_3_days: {
    label: 'Aviso de Vencimiento (3 Días)',
    helperText: 'Último aviso, se envía 3 días antes de que la suscripción expire.',
  },
  subscription_expired: {
    label: 'Suscripción Vencida',
    helperText: 'Se envía justo cuando la suscripción ha llegado a su fecha de fin.',
  },
  subscription_renewed: {
    label: 'Suscripción Renovada',
    helperText: 'Se envía cuando una suscripción vencida se reactiva.',
  },
};

// Componente de información sobre el comportamiento de las plantillas
const TemplateInfo = () => (
  <Paper variant="outlined" sx={{ 
    p: 3, 
    backgroundColor: THEME_COLORS.BACKGROUND_DEFAULT, 
    borderRadius: '12px',
    border: `1px solid ${THEME_COLORS.BORDER_LIGHT}`,
    mb: 2 
  }}>
    <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
      <InfoIcon sx={{ color: THEME_COLORS.TEXT_SECONDARY, mt: 0.5 }} />
      <Box>
        <Typography variant="body2" sx={{ fontWeight: 600, color: THEME_COLORS.TEXT_PRIMARY, mb: 1 }}>
          💡 Comportamiento de las Plantillas de Suscripción
        </Typography>
        <Typography variant="caption" sx={{ color: THEME_COLORS.TEXT_SECONDARY, lineHeight: 1.5 }}>
          • Las plantillas se activan automáticamente según los eventos de suscripción<br/>
          • Puedes desactivar plantillas específicas (el texto se conserva)<br/>
          • Solo las plantillas activas enviarán notificaciones automáticas<br/>
          • Las variables se reemplazan automáticamente con datos reales del contacto y suscripción
        </Typography>
      </Box>
    </Box>
  </Paper>
);

const SubscriptionTemplates = () => {
  const {
    templates,
    loading,
    error,
    loadTemplates,
    updateAllTemplates,
    operationStatus,
    operatingTemplateName,
    resetOperationState,
  } = useSubscriptionTemplates();

  const [editableTemplates, setEditableTemplates] = useState([]);
  const [showOperationModal, setShowOperationModal] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  
  // Referencias a los textareas para insertar variables
  const textareaRefs = useRef({});

  useEffect(() => {
    loadTemplates();
  }, [loadTemplates]);

  useEffect(() => {
    if (templates.length > 0) {
      setEditableTemplates(templates);
    }
  }, [templates]);

  const handleTemplateChange = (templateId, field, value) => {
    setEditableTemplates(prev =>
      prev.map(t =>
        t.template_id === templateId ? { ...t, [field]: value } : t
      )
    );
  };

  const handleSave = async () => {
    setShowOperationModal(true);
    const result = await updateAllTemplates(editableTemplates);
    
    const delay = result.success ? UX_TIMING.SUCCESS_DISPLAY_TIME : UX_TIMING.ERROR_DISPLAY_TIME;
    
    setTimeout(() => {
      setShowOperationModal(false);
      resetOperationState();
    }, delay);
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleVariableSelect = (variable) => {
    // Buscar el textarea actualmente enfocado
    const focusedElement = document.activeElement;
    
    if (focusedElement && focusedElement.tagName === 'TEXTAREA') {
      // Si hay un textarea enfocado, insertar ahí
      subscriptionTemplateVariableService.insertVariableInTextarea(variable, focusedElement);
    } else {
      // Si no hay focus específico, insertar en el primer textarea disponible
      const firstTextarea = Object.values(textareaRefs.current)[0];
      if (firstTextarea) {
        subscriptionTemplateVariableService.insertVariableInTextarea(variable, firstTextarea);
      }
    }
  };
  
  const renderTemplateCards = () => {
    if (loading && templates.length === 0) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      );
    }

    if (error) {
      return <Alert severity="error" sx={{ m: 2 }}>{error}</Alert>;
    }

    return (
      <Grid container spacing={3}>
        {editableTemplates.map(template => (
          <Grid item xs={12} key={template.template_id}>
            <Paper 
              elevation={0} 
              sx={{ 
                p: 3, 
                border: `1px solid ${THEME_COLORS.BORDER_LIGHT}`, 
                borderRadius: '12px'
              }}
            >
              <Typography variant="subtitle1" gutterBottom sx={{ 
                fontWeight: 600, 
                color: THEME_COLORS.TEXT_PRIMARY,
                fontSize: '1.1rem',
                mb: 2
              }}>
                {templateMetadata[template.event_type]?.label || template.event_type}
              </Typography>
              
              <TextField
                inputRef={el => textareaRefs.current[template.template_id] = el}
                fullWidth
                multiline
                rows={6}
                label="Mensaje de la plantilla"
                value={template.message_body}
                onChange={(e) => handleTemplateChange(template.template_id, 'message_body', e.target.value)}
                placeholder="Ej: ¡Hola {{nombre_contacto}}! Tu suscripción {{suscripcion_plan}} ha sido activada exitosamente..."
                helperText={templateMetadata[template.event_type]?.helperText || ''}
                variant="outlined"
                sx={{
                  mb: 2,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '12px',
                    backgroundColor: THEME_COLORS.BACKGROUND_PAPER,
                    '& textarea': {
                      fontFamily: 'monospace',
                      fontSize: '0.9rem'
                    }
                  }
                }}
              />
              
              <FormControlLabel
                control={
                  <Switch
                    checked={!!template.is_active}
                    onChange={(e) => handleTemplateChange(template.template_id, 'is_active', e.target.checked)}
                  />
                }
                label={
                  template.is_active 
                    ? "Plantilla activa" 
                    : "Plantilla inactiva"
                }
                sx={{ 
                  color: template.is_active 
                    ? THEME_COLORS.TEXT_PRIMARY
                    : THEME_COLORS.TEXT_SECONDARY
                }}
              />
            </Paper>
          </Grid>
        ))}
      </Grid>
    );
  };

  return (
    <>
      <Paper
        elevation={0}
        sx={{
          mb: 4,
          border: `1px solid ${THEME_COLORS.BORDER_LIGHT}`,
          borderRadius: '16px',
          overflow: 'hidden',
        }}
      >
        <Accordion>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon sx={{ color: THEME_COLORS.TEXT_SECONDARY }} />}
            sx={{
              backgroundColor: THEME_COLORS.BACKGROUND_DEFAULT,
              borderBottom: `1px solid ${THEME_COLORS.BORDER_LIGHT}`,
              py: 2,
              '& .MuiAccordionSummary-content': {
                display: 'flex',
                alignItems: 'center',
                gap: 2,
              },
            }}
          >
            <Box sx={{ width: 48, height: 48, borderRadius: '12px', backgroundColor: THEME_COLORS.TEXT_PRIMARY, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <MessageIcon sx={{ color: THEME_COLORS.WHITE, fontSize: '1.5rem' }} />
            </Box>
            <Box>
              <Typography variant="h6" sx={{ color: THEME_COLORS.TEXT_PRIMARY, fontWeight: 600 }}>
                Plantillas de Notificaciones de Suscripción
              </Typography>
              <Typography variant="body2" sx={{ color: THEME_COLORS.TEXT_SECONDARY, mt: 0.5 }}>
                Configura los mensajes automáticos para eventos de suscripción
              </Typography>
            </Box>
          </AccordionSummary>
          
          <AccordionDetails sx={{ p: 0, backgroundColor: THEME_COLORS.WHITE }}>
            {/* Información sobre comportamiento */}
            <Box sx={{ p: 3, pb: 0 }}>
              <TemplateInfo />
            </Box>
            
            {/* Sistema de Tabs */}
            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
              <Tabs 
                value={activeTab} 
                onChange={handleTabChange}
                sx={{ 
                  px: 3,
                  '& .MuiTab-root': {
                    textTransform: 'none',
                    fontWeight: 600,
                    fontSize: '0.95rem'
                  }
                }}
              >
                <Tab 
                  icon={<Code sx={{ color: THEME_COLORS.TEXT_SECONDARY }} />} 
                  label="Editar Plantillas" 
                  sx={{ 
                    minHeight: 48,
                    color: THEME_COLORS.TEXT_SECONDARY,
                    '&.Mui-selected': { color: THEME_COLORS.TEXT_PRIMARY }
                  }}
                />
                <Tab 
                  icon={<Visibility sx={{ color: THEME_COLORS.TEXT_SECONDARY }} />} 
                  label="Variables Disponibles" 
                  sx={{ 
                    minHeight: 48,
                    color: THEME_COLORS.TEXT_SECONDARY,
                    '&.Mui-selected': { color: THEME_COLORS.TEXT_PRIMARY }
                  }}
                />
              </Tabs>
            </Box>

            {/* Contenido de las Tabs */}
            {activeTab === 0 && (
              <Box sx={{ p: 3 }}>
                {renderTemplateCards()}
                
                <Divider sx={{ my: 4 }} />
                
                <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <Button
                    variant="contained"
                    startIcon={<SaveIcon />}
                    onClick={handleSave}
                    disabled={operationStatus === OPERATION_STATUS.LOADING}
                    size="large"
                    sx={{
                      backgroundColor: THEME_COLORS.TEXT_PRIMARY,
                      color: THEME_COLORS.BACKGROUND_PAPER,
                      borderRadius: '12px',
                      px: 4,
                      py: 1.5,
                      textTransform: 'none',
                      fontWeight: 600,
                      minWidth: 160,
                      '&:hover': { 
                        backgroundColor: '#1a1a1a',
                        transform: 'translateY(-1px)'
                      }
                    }}
                  >
                    {operationStatus === OPERATION_STATUS.LOADING ? 'Guardando...' : 'Guardar Plantillas'}
                  </Button>
                </Box>
              </Box>
            )}

            {activeTab === 1 && (
              <Box sx={{ p: 3 }}>
                <SubscriptionTemplateVariablesPicker 
                  onVariableSelect={handleVariableSelect}
                />
              </Box>
            )}
          </AccordionDetails>
        </Accordion>
      </Paper>

      {/* Modal de Operación */}
      <ConfirmationModal
        open={showOperationModal}
        status={operationStatus}
        title={
          operationStatus === OPERATION_STATUS.LOADING 
            ? 'Guardando Plantillas'
            : operationStatus === OPERATION_STATUS.SUCCESS 
              ? '¡Plantillas Guardadas!'
              : 'Error al Guardar'
        }
        subtitle={
          operationStatus === OPERATION_STATUS.LOADING 
            ? 'Por favor espera mientras guardamos las plantillas...'
            : operationStatus === OPERATION_STATUS.SUCCESS 
              ? `Las ${operatingTemplateName} se han actualizado correctamente.`
              : 'Hubo un problema al guardar los cambios.'
        }
        userName={operatingTemplateName}
        hideActions={true}
      />
    </>
  );
};

export default SubscriptionTemplates; 